import React, { useEffect, useRef, useState, useCallback } from "react";
import styleClasses from '../css/mobile/CDPlayer.module.scss';

const CDPlayer = ({ videoId, videoTitle, styleData, thumbnail }) => {
  const playerRef = useRef(null);
  const progressRingRef = useRef(null);
  const progressBarRef = useRef(null);
  const cdBackgroundRef = useRef(null);
  const [thumbnailUrl, setThumbnailUrl] = useState("");
  const [isPlaying, setIsPlaying] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const animationFrameRef = useRef(null);
  const [playProceed, setPlayProceed] = useState('00:00');
  const [playDuration, setPlayDuration] = useState('00:00');
  const [playPercent, setPlayPercent] = useState('0');
  const [mouseOverTime, setMouseOverTime] = useState('');
  const [mouseOverDisplay, setMouseOverDisplay] = useState('none');
  const [mouseOverPercent, setMouseOverPercent] = useState('0');
  const [titleState, setTitleState] = useState(videoTitle || "");
  const isSoundCloud = videoId?.includes("soundcloud.com");
  const [soundCloudDuration, setSoundCloudDuration] = useState(0);
  const widgetRef = useRef(null);
  const iframeRef = useRef(null);

  useEffect(() => {
    // 썸네일 URL 설정
    setThumbnailUrl(
      thumbnail || (isSoundCloud ? "/assets/images/Background Test 1.png" : `https://img.youtube.com/vi/${videoId}/0.jpg`)
    );
    // YouTube API 로드 (최적화)
    const loadYouTubeAPI = () => {
      if (!window.YT) {
        const script = document.createElement("script");
        script.src = "https://www.youtube.com/iframe_api";
        script.async = true;
        document.body.appendChild(script);
      }
    };

      // SoundCloud 위젯 API 로드
      const loadSoundCloudAPI = () => {
        if (!window.SC) {
          const script = document.createElement("script");
          script.src = "https://w.soundcloud.com/player/api.js";
          script.async = true;
          document.body.appendChild(script);
        }
      };

    // 진행률 업데이트 (requestAnimationFrame 사용)
    const updateProgressRing = () => {
      if (!playerRef.current || !progressRingRef.current) return;
      const currentTime = playerRef.current.getCurrentTime();
      const duration = playerRef.current.getDuration();
      const progressPercent = (currentTime / duration) * 100;
      progressRingRef.current.style.background = `conic-gradient(white ${progressPercent}%, transparent ${progressPercent}%)`;
      animationFrameRef.current = requestAnimationFrame(updateProgressRing);

      setPlayDuration(formatTime(parseInt(duration)));
      setPlayProceed(formatTime(parseInt(currentTime)));
      setPlayPercent(progressPercent);
    };


    const updateVideoTitle = () => {
      if (!videoTitle && playerRef.current) { // videoTitle이 없을 때만 가져옴
        const videoData = playerRef.current.getVideoData();
        if (videoData?.title) {
          setTitleState(videoData.title);
        }
      }
    };

    // YouTube 플레이어 초기화
    const initializeYouTube = () => {
      if (playerRef.current) return; // 이미 초기화된 경우 실행 방지

      playerRef.current = new window.YT.Player("youtube-player", {
        videoId,
        playerVars: {
          autoplay: 1,
          controls: 0,
          mute: 0,
          loop: 1,
          playlist: videoId,
        },
        events: {
          onReady: () => {
            setIsInitialized(true);
            updateVideoTitle(); // 제목 업데이트
            animationFrameRef.current = requestAnimationFrame(updateProgressRing); // 시작
          },
          onStateChange: (event) => {
            if (event.data === window.YT.PlayerState.PLAYING) {
              setIsPlaying(true);
              cdBackgroundRef.current.style.animationPlayState = "running";
            } else if (event.data === window.YT.PlayerState.PAUSED) {
              setIsPlaying(false);
              cdBackgroundRef.current.style.animationPlayState = "paused";
            }
          },
        },
      });
    };

    const initializeSoundCloud = () => {
      const widget = window.SC.Widget(iframeRef.current);
      widgetRef.current = widget;
    
      // 위젯 준비 완료
      widget.bind(window.SC.Widget.Events.READY, () => {
        setIsInitialized(true);
    
        // duration을 따로 가져와서 저장
        widget.getDuration((duration) => {
          if (typeof duration === 'number' && duration > 0) {
            setSoundCloudDuration(duration); // 밀리초(ms)
            setPlayDuration(formatTime(duration / 1000)); // 초기값 설정
          }
        });
      });
    
      // 재생 이벤트
      widget.bind(window.SC.Widget.Events.PLAY, () => {
        setIsPlaying(true);
        if (cdBackgroundRef.current) {
          cdBackgroundRef.current.style.animationPlayState = "running";
        }
      });
    
      // 일시정지 이벤트
      widget.bind(window.SC.Widget.Events.PAUSE, () => {
        setIsPlaying(false);
        if (cdBackgroundRef.current) {
          cdBackgroundRef.current.style.animationPlayState = "paused";
        }
      });
    
      // 재생 진행 중 이벤트
      widget.bind(window.SC.Widget.Events.PLAY_PROGRESS, (e) => {
        const currentSec = e.currentPosition / 1000;
        const durationSec = soundCloudDuration / 1000;
        const percent = e.relativePosition * 100;
    
        if (progressRingRef.current) {
          progressRingRef.current.style.background = `conic-gradient(white ${percent}%, transparent ${percent}%)`;
        }
    
        setPlayProceed(formatTime(currentSec));
        setPlayPercent(percent);
    
        // durationSec은 getDuration 이후에만 유효함
        if (Number.isFinite(durationSec) && durationSec > 0) {
          setPlayDuration(formatTime(durationSec));
        }
      });
    };

    if (isSoundCloud) {
      loadSoundCloudAPI();
      const interval = setInterval(() => {
        if (window.SC && iframeRef.current) {
          clearInterval(interval);
          initializeSoundCloud();
        }
      }, 200);
    } else {
      if (window.YT && typeof window.YT.Player === "function") {
        initializeYouTube();
      } else {
        loadYouTubeAPI();
        window.onYouTubeIframeAPIReady = initializeYouTube;
      }
    }

    return () => {
      if (playerRef.current) {
        playerRef.current.destroy();
        playerRef.current = null;
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [videoId, videoTitle, thumbnail]);

  function formatTime(seconds) {
    if (!Number.isFinite(seconds)) return "00:00";
    const wholeSeconds = Math.floor(seconds); // 소수점 버림
    const minutes = Math.floor(wholeSeconds / 60);
    const remainingSeconds = wholeSeconds % 60;
    return `${String(minutes).padStart(2, "0")}:${String(remainingSeconds).padStart(2, "0")}`;
  }

  // 재생 / 일시정지 토글
  const togglePlayPause = useCallback(() => {
    if (!isInitialized) return;

    if (isSoundCloud && widgetRef.current) {
      widgetRef.current.isPaused((paused) => {
        if (paused) widgetRef.current.play();
        else widgetRef.current.pause();
      });
    } else if (playerRef.current) {
      const state = playerRef.current.getPlayerState();
      if (state === window.YT.PlayerState.PLAYING) {
        playerRef.current.pauseVideo();
      } else {
        playerRef.current.playVideo();
      }
    }
  }, [isInitialized, isSoundCloud]);

  // (MouseEnter) 재생 로딩 바
  const handleMouseEnter = (event) => {
    if (progressBarRef.current) {
      progressBarRef.current.addEventListener('mousemove', (event) => {
        let progressBarWidth = progressBarRef.current.offsetWidth;

        if (playerRef.current) {
          const duration = playerRef.current.getDuration();
          const percentage = event.offsetX / progressBarWidth * 100;
          const seekTime = duration * percentage;
          setMouseOverPercent(percentage);
          setMouseOverTime(formatTime(parseInt(seekTime)));
        }
      });
    }
    setMouseOverDisplay('block');
  };
  // (MouseMove) 재생 로딩 바
  const handleMouseMove = (event) => {
    if (!progressBarRef.current) return;
    const rect = progressBarRef.current.getBoundingClientRect();
    const percent = (event.clientX - rect.left) / rect.width;
  
    if (isSoundCloud && soundCloudDuration > 0) {
      const seekTime = (soundCloudDuration * percent) / 1000;
      setMouseOverPercent(percent * 100);
      setMouseOverTime(formatTime(seekTime));
    } else if (playerRef.current) {
      const duration = playerRef.current.getDuration();
      const seekTime = duration * percent;
      setMouseOverPercent(percent * 100);
      setMouseOverTime(formatTime(seekTime));
    }
  
    setMouseOverDisplay("block");
  };
  
  // (Click) 재생 로딩 바
  const clickPlayLoadingBar = useCallback((event) => {
    if (!isInitialized || !progressBarRef.current) return;
  
    const progressBarRect = progressBarRef.current.getBoundingClientRect();
    const progressBarWidth = progressBarRect.width;
    const clickX = event.clientX - progressBarRect.left;
    const percent = clickX / progressBarWidth;
  
    if (isSoundCloud && widgetRef.current && soundCloudDuration > 0) {
      const seekToMs = soundCloudDuration * percent;
      widgetRef.current.seekTo(seekToMs); // ms 단위로 점프
    } else if (playerRef.current) {
      const duration = playerRef.current.getDuration();
      const seekTime = duration * percent;
      playerRef.current.seekTo(seekTime, true);
      playerRef.current.playVideo();
    }
  }, [isInitialized, isSoundCloud, soundCloudDuration]);

  // const host = window.location.host;
  // const production = host.includes('localhost') ? false : true;

  return (
    <div className={ styleClasses.cdWrap }>
      <div className={`${ styleClasses.cdDisplayMobile } ${ isPlaying && styleClasses.play }`}>
        <div className={styleClasses.progressRing} ref={progressRingRef}></div>
        <div className={styleClasses.cd}>
          <div
            className={styleClasses.cdBackground}
            ref={cdBackgroundRef}
            style={{ backgroundImage: `url(${thumbnailUrl})` }}
          ></div>
          <div className={styleClasses.centerLabel}>
            <button className={styleClasses.playPauseBtn} onClick={togglePlayPause}>
              {isPlaying ? (
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="50" height="50">
                  <path fill="white" d="M6 19h4V5H6zm8-14v14h4V5z" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="50" height="50">
                  <path fill="white" d="M8 5v14l11-7z" />
                </svg>
              )}
            </button>
          </div>
        </div>
        {isSoundCloud ? (
          <iframe
            ref={iframeRef}
            width="0"
            height="166"
            scrolling="no"
            frameBorder="no"
            allow="autoplay"
            src={`https://w.soundcloud.com/player/?url=${encodeURIComponent(videoId)}&auto_play=false&hide_related=true&show_comments=false&show_user=false&show_reposts=false&visual=false`}
            style={{ position: 'absolute', zIndex: 0 }}
          />
        ) : (
          <div id="youtube-player" style={{ 'position': 'absolute', 'zIndex': 0, 'height': 0 }}></div>
        )}
      </div>
      <div className={`${ styleClasses.musicDisplay } ${ isPlaying && styleClasses.play } ${ styleData !== true && styleClasses.white }`}>
        {/*<p className={ styleClasses.title }>{titleState}</p>*/}
        <div className={styleClasses.titleContainer}><div className={ titleState.length > 12 ? styleClasses.titleAnimated : styleClasses.titleStatic }>{titleState}</div></div>
        <ul className={ styleClasses.playDisplay }>
          <li className={ styleClasses.playTime }>
            <span>{ playProceed }</span>
            <span>{ playDuration }</span>
          </li>
          <li ref={ progressBarRef }
              className={ styleClasses.playProgress }
              onClick={(event) => clickPlayLoadingBar(event)}
              onMouseEnter={ handleMouseEnter }
              onMouseLeave={ () => setMouseOverDisplay('none') }
              onMouseMove={ (event) => handleMouseMove(event) }
          >
            <span style={{ width: playPercent + '%' }}></span>
            <div className={ styleClasses.mouseOverTime } style={{ left: mouseOverPercent + '%', display: mouseOverDisplay }} >
              <p>{ mouseOverTime }</p>
            </div>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default CDPlayer;
